import { GlobalPlatformPermissionsPanel } from './GlobalPlatformPermissionsPanel'
import { locationDisplayAddress } from '../../../services/locationAddress'
import React, { useEffect, useMemo, useState } from 'react'
import {
  assignOrganizationUserRole,
  createScopedRoleAssignment,
  createTenantRole,
  createOrganizationUser,
  deleteTenantRole,
  inviteOrganizationUser,
  listAdminCapabilities,
  listOrganizationInvites,
  listOrganizationRoles,
  listOrganizationUserAccess,
  listOrganizationUsersPage,
  resetOrganizationUserPassword,
  revokeOrganizationInvite,
  revokeOrganizationUserRole,
  updateTenantRole,
  updateOrganizationUser,
} from '../../../services/access'
import type {
  AccessibleLocation,
  AdminCapabilitySummary,
  Organization,
  OrganizationUnit,
  OrganizationUserInvitePayload,
  OrganizationInviteSummary,
  OrganizationUserRolePayload,
  OrganizationUserSummary,
  ScopedRoleAssignment,
  TenantRoleSummary,
} from '../../../types/access'
import { useAccessContext } from '../../../context/AccessContext'
import { useAuth } from '../../../context/AuthContext'
import { formatScopeLabel } from '../../../utils/displayLabels'
import { formatDate, formatOrgType } from './adminPanelUtils'
import { ConfirmDialog, DetailDrawer, EmptyState, EntityActionMenu, ErrorBanner, FormModal, LoadingState, PaginationControls, SortableHeader } from './AdminUiPrimitives'
import { usePaginatedList } from './usePaginatedList'
import { useI18n } from '../../../i18n/I18nContext'

type TFunc = (text: string, vars?: Record<string, string | number>) => string

interface UsersAccessPanelProps {
  organizations: Organization[]
  organizationUnits: OrganizationUnit[]
  accessibleLocations: AccessibleLocation[]
  selectedOrgId: string
  canViewUsers: boolean
  canManageUsers: boolean
  canManageRoles: boolean
  onSelectOrg: (orgId: string) => void
  onRefresh: () => Promise<void>
}

type TabId = 'users' | 'invitations' | 'roles' | 'scope' | 'audit'
type UserSegmentId = 'admins' | 'workers' | 'inactive'

const tabs: Array<{ id: TabId; label: string }> = [
  { id: 'users', label: 'Users' },
  { id: 'invitations', label: 'Invitations' },
  { id: 'roles', label: 'Roles' },
  { id: 'scope', label: 'Scope Assignments' },
  { id: 'audit', label: 'Audit / Recent Changes' },
]

const titleCase = (value: string) => value.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')

const userName = (user: OrganizationUserSummary) => [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || user.phone || user.id
const roleSummary = (user: OrganizationUserSummary, t: TFunc) => (user.roles || [])
  .map((role) => String(role.roleLabel || role.roleKey || role.roleId || t('Role')))
  .filter(Boolean)
  .slice(0, 3)
  .join(', ') || t('No scoped roles')
const scopeSummary = (user: OrganizationUserSummary, t: TFunc) => (user.scopes || [])
  .map((scope) => t(titleCase(String(scope.scopeType || 'scope'))))
  .filter(Boolean)
  .slice(0, 3)
  .join(', ') || t('Organization visibility')
const workerProfileIdForUser = (user: OrganizationUserSummary) => user.workerProfileId || user.worker_profile_id || null
const workerDisplayNameForUser = (user: OrganizationUserSummary) => user.workerDisplayName || user.worker_display_name || userName(user)
const workerRelationshipTypeForUser = (user: OrganizationUserSummary) => user.workerRelationshipType || user.worker_relationship_type || ''
const workerOrganizationNameForUser = (user: OrganizationUserSummary) => user.workerOrganizationName || user.worker_organization_name || ''
const userLooksWorker = (user: OrganizationUserSummary) => user.membership_type === 'worker' || Boolean(workerProfileIdForUser(user))
const userLooksAdmin = (user: OrganizationUserSummary) => (user.roles || []).length > 0 || user.membership_type === 'corporate_user' || user.membership_type === 'admin'
const userAccessBadges = (user: OrganizationUserSummary, t: TFunc) => {
  const badges: string[] = []
  if (userLooksWorker(user)) badges.push(t('Worker'))
  if (userLooksAdmin(user)) {
    const roleText = roleSummary(user, t).toLowerCase()
    badges.push(roleText.includes('supervisor') || roleText.includes('manager') ? t('Supervisor') : t('Administrator'))
  }
  return Array.from(new Set(badges))
}
const userSegmentLabel = (segment: UserSegmentId) => {
  if (segment === 'workers') return 'Worker App Access'
  if (segment === 'inactive') return 'Invited / Disabled'
  return 'Admins & Supervisors'
}
const roleLabel = (role: TenantRoleSummary | null | undefined, t: TFunc) => {
  if (!role) return ''
  const label = role.label || role.role_key || role.id
  const permissionCount = Number(role.permission_count || 0)
  return permissionCount > 0 ? t('{label} ({count} permissions)', { label, count: permissionCount }) : label
}
const protectedPlatformRoleKeys = new Set(['super_admin', 'system_admin', 'system_super_admin', 'platform_admin', 'platform_billing_admin'])
const isProtectedPermission = (permissionKey: string) => permissionKey.startsWith('system.') || permissionKey.startsWith('platform.') || permissionKey === 'tenant.platform.manage'
const isProtectedRole = (role?: TenantRoleSummary | null) => Boolean(
  role && (protectedPlatformRoleKeys.has(String(role.role_key || '')) || (role.permission_keys || []).some(isProtectedPermission))
)
const scopeTypeLabel = (scopeType?: string): string => {
  if (scopeType === 'organization_unit') return 'Organization unit'
  if (scopeType === 'location') return 'Location'
  if (scopeType === 'tenant') return 'Tenant'
  return 'Organization'
}
const scopeGroupLabel = (scopeType?: string) => {
  if (scopeType === 'organization_unit') return 'Org Unit'
  if (scopeType === 'location') return 'Location'
  if (scopeType === 'tenant') return 'Tenant'
  return 'Organization'
}
const blankInviteForm = (): OrganizationUserInvitePayload => ({
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  membershipType: 'corporate_user',
  scopeType: 'organization',
  scopeId: null,
  inheritsDown: true,
})
const blankRoleForm = (): OrganizationUserRolePayload => ({
  roleId: '',
  scopeType: 'organization',
  scopeId: null,
  inheritsDown: true,
})
const blankTenantRoleForm = () => ({
  id: '',
  roleKey: '',
  label: '',
  description: '',
  permissionKeys: [] as string[],
})

const featurePermissionGroups = [
  { feature: 'Organizations', keys: ['tenant.organizations.view'], source: 'Organization directory and context views.' },
  { feature: 'Users & Access', keys: ['tenant.users.view', 'tenant.roles.view'], source: 'User lists, role catalog, and scoped assignments.' },
  { feature: 'Workers', keys: ['tenant.workers.view', 'tenant.job_assignments.view'], source: 'Worker directory and detail visibility: direct, inherited from tenant, organization, parent OU, or location; blocked when worker visibility is missing or the worker is outside scope.' },
  { feature: 'Worker Availability', keys: ['tenant.workers.view', 'tenant.job_assignments.view'], source: 'Visible to anyone who can read the worker profile through the backend worker resource resolver; blocked only for missing worker visibility or outside scope.' },
  { feature: 'Worker Reliability / Rating', keys: ['tenant.workers.view', 'tenant.job_assignments.view'], source: 'Reliability and attendance events are visible to anyone who can read the worker profile; reviews still use the centralized worker review scope.' },
  { feature: 'Jobs / Scheduling', keys: ['tenant.jobs.view', 'tenant.job_assignments.view'], source: 'Jobs, schedule board, assignments, and top-workers visibility inherit through tenant, organization, OU, and location resource containment.' },
  { feature: 'Locations', keys: ['tenant.locations.view'], source: 'Scoped location, geofence, and location context visibility can be direct or inherited from tenant, organization, or parent OU.' },
  { feature: 'Timesheets / Payroll', keys: ['tenant.timesheets.view', 'tenant.payroll.export'], source: 'Timesheet queues and payroll/export surfaces.' },
]

export const UsersAccessPanel: React.FC<UsersAccessPanelProps> = ({
  organizations,
  organizationUnits,
  accessibleLocations,
  selectedOrgId,
  canViewUsers,
  canManageUsers,
  canManageRoles,
  onSelectOrg,
  onRefresh,
}) => {
  const { accessContext, hasAnyPermission } = useAccessContext()
  const { logout } = useAuth()
  const { t }: { t: TFunc } = useI18n()
  // Inviting users is allowed for invite-capable roles even when they lack full
  // user management; management actions (add/edit/suspend/delete) stay on canManageUsers.
  const canInviteUsers = canManageUsers || hasAnyPermission(['tenant.users.invite', 'tenant.users.manage'])
  const [activeTab, setActiveTab] = useState<TabId>('users')
  const [activeSegment, setActiveSegment] = useState<UserSegmentId>('admins')
  const [invites, setInvites] = useState<OrganizationInviteSummary[]>([])
  const [roles, setRoles] = useState<TenantRoleSummary[]>([])
  const [capabilities, setCapabilities] = useState<AdminCapabilitySummary[]>([])
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedUserAccess, setSelectedUserAccess] = useState<ScopedRoleAssignment[]>([])
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [inviteLink, setInviteLink] = useState('')
  const [showInvite, setShowInvite] = useState(false)
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [showTenantRoleModal, setShowTenantRoleModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showUserDrawer, setShowUserDrawer] = useState(false)
  const [confirmStatus, setConfirmStatus] = useState<{ user: OrganizationUserSummary; status: 'active' | 'disabled' } | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState<ScopedRoleAssignment | null>(null)
  const [confirmDeleteRole, setConfirmDeleteRole] = useState<TenantRoleSummary | null>(null)
  const [confirmInviteRevoke, setConfirmInviteRevoke] = useState<OrganizationInviteSummary | null>(null)
  const [resetPasswordFor, setResetPasswordFor] = useState<OrganizationUserSummary | null>(null)
  const [globalPermissionsFor, setGlobalPermissionsFor] = useState<OrganizationUserSummary | null>(null)
  const [inviteForm, setInviteForm] = useState<OrganizationUserInvitePayload>(blankInviteForm())
  const [roleForm, setRoleForm] = useState<OrganizationUserRolePayload>(blankRoleForm())
  const [tenantRoleForm, setTenantRoleForm] = useState(blankTenantRoleForm())
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', phone: '' })
  const [passwordForm, setPasswordForm] = useState({ password: '' })
  const userList = usePaginatedList<OrganizationUserSummary, { status?: string }>({
    defaultSortBy: 'name',
    defaultFilters: {},
    enabled: Boolean(selectedOrgId && canViewUsers),
    fetchPage: (query, signal) => selectedOrgId && canViewUsers
      ? listOrganizationUsersPage(selectedOrgId, { ...query }, signal)
      : Promise.resolve({
        items: [],
        pagination: { page: query.page || 1, pageSize: query.pageSize || 25, total: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false },
        sort: { sortBy: query.sortBy, sortDir: query.sortDir || 'asc' },
        filters: { ...query },
      }),
  })
  const users = userList.items

  const selectedOrg = organizations.find((org) => org.id === selectedOrgId)
  const selectedUser = users.find((user) => user.id === selectedUserId) || null
  const canUseProtectedPlatformRoles = Boolean(accessContext?.isSystemAdmin || accessContext?.isSystemSuperAdmin)
  const canAssignTenantScope = Boolean(accessContext?.canManageTenantRoles || accessContext?.isSystemSuperAdmin)
  const canSetGlobalPassword = Boolean(accessContext?.isSystemSuperAdmin)
  const canEditSelectedGlobalProfile = Boolean(canSetGlobalPassword || (selectedUser && selectedUser.id === accessContext?.currentUser?.id))
  const scopeLookupContext = {
    tenant: accessContext?.tenant,
    tenantId: accessContext?.tenantId,
    organizations,
    organizationUnits,
    locations: accessibleLocations,
  }
  const segmentedUsers = useMemo(() => {
    const inactive = users.filter((user) => user.membership_status === 'invited' || user.membership_status === 'disabled' || user.active === false)
    const activeUsers = users.filter((user) => !inactive.some((inactiveUser) => inactiveUser.id === user.id))
    const admins = activeUsers.filter(userLooksAdmin)
    const workers = activeUsers.filter(userLooksWorker)
    return { admins, workers, inactive }
  }, [users])
  const displayedUsers = activeTab === 'invitations'
    ? users.filter((user) => user.membership_status === 'invited')
    : segmentedUsers[activeSegment]
  const roleOptions = useMemo(
    () => roles
      .filter((role) => canUseProtectedPlatformRoles || !isProtectedRole(role))
      .filter((role) => role.role_key !== 'tenant_full_admin' || canAssignTenantScope)
      .map((role) => ({ id: role.id, label: roleLabel(role, t), key: role.role_key, description: role.description })),
    [roles, canAssignTenantScope, canUseProtectedPlatformRoles],
  )
  const organizationRoleOptions = useMemo(
    () => roleOptions.filter((role) => role.key !== 'tenant_full_admin'),
    [roleOptions],
  )
  const capabilitiesByGroup = useMemo(() => {
    const grouped = new Map<string, AdminCapabilitySummary[]>()
    for (const capability of capabilities) {
      const permissionKeys = canUseProtectedPlatformRoles
        ? capability.permissionKeys
        : capability.permissionKeys.filter((permissionKey) => !isProtectedPermission(permissionKey))
      if (permissionKeys.length === 0) continue
      const group = capability.group || 'Admin'
      grouped.set(group, [...(grouped.get(group) || []), { ...capability, permissionKeys }])
    }
    return Array.from(grouped.entries())
  }, [capabilities, canUseProtectedPlatformRoles])

  const getScopeOptions = (scopeType?: OrganizationUserRolePayload['scopeType']) => {
    if (scopeType === 'organization') return organizations.filter((org) => org.id === selectedOrgId).map((org) => ({ id: org.id, label: `${org.name} - ${t(formatOrgType(org.org_type))}`, disabled: false }))
    if (scopeType === 'organization_unit') return organizationUnits.filter((unit) => unit.org_id === selectedOrgId).map((unit) => ({ id: unit.id, label: `${unit.name} - ${t(titleCase(unit.ou_type))}`, disabled: false }))
    if (scopeType === 'location') return accessibleLocations
      .filter((location) => (location.ownerOrgId || location.owner_org_id) === selectedOrgId)
      .map((location) => ({
        id: location.id,
        label: [location.name || location.label || location.location_key || t('Location'), locationDisplayAddress(location)].filter(Boolean).join(' - '),
        disabled: false,
      }))
    return []
  }
  const roleScopeOptions = useMemo(
    () => getScopeOptions(roleForm.scopeType || 'organization'),
    [accessibleLocations, organizationUnits, organizations, roleForm.scopeType, selectedOrgId],
  )
  const inviteScopeOptions = useMemo(
    () => getScopeOptions(inviteForm.scopeType || 'organization'),
    [accessibleLocations, inviteForm.scopeType, organizationUnits, organizations, selectedOrgId],
  )
  const scopeLabelForAssignment = (assignment: ScopedRoleAssignment) => {
    return formatScopeLabel(assignment, scopeLookupContext)
  }
  const scopeImpactLabel = (scopeType?: string, inheritsDown?: boolean) => {
    if (scopeType === 'tenant') return 'Tenant-wide access across every organization in this tenant.'
    if (scopeType === 'organization') return inheritsDown === false ? 'Direct organization access only.' : 'Inherited through this organization, its OUs, and linked locations where backend rules allow.'
    if (scopeType === 'organization_unit') return inheritsDown === false ? 'Direct OU access only.' : 'Inherited through child OUs and linked locations.'
    if (scopeType === 'location') return 'Location-scoped access for this work site.'
    return 'Scoped access in the selected organization context.'
  }
  const selectedUserPermissionGrants = useMemo(() => {
    const roleById = new Map(roles.map((role) => [role.id, role]))
    return selectedUserAccess.flatMap((assignment) => {
      const role = roleById.get(assignment.role_id)
      return (role?.permission_keys || []).map((permissionKey) => ({
        permissionKey,
        roleLabel: assignment.role_label || role?.label || assignment.role_key || role?.role_key || t('Role'),
        scopeLabel: scopeLabelForAssignment(assignment),
      }))
    })
  }, [roles, selectedUserAccess, organizations, organizationUnits, accessibleLocations, accessContext?.tenant, accessContext?.tenantId])
  const selectedUserPermissionKeys = useMemo(
    () => new Set(selectedUserPermissionGrants.map((grant) => grant.permissionKey)),
    [selectedUserPermissionGrants],
  )
  const currentAdminPermissionKeys = useMemo(
    () => new Set(accessContext?.effectivePermissionKeys || []),
    [accessContext?.effectivePermissionKeys],
  )
  const currentAdminGrantByKey = useMemo(() => {
    const byKey = new Map<string, string>()
    for (const grant of accessContext?.effectivePermissions || []) {
      if (!byKey.has(grant.permissionKey)) {
        byKey.set(grant.permissionKey, `${grant.roleLabel || grant.roleKey} / ${formatScopeLabel({ scope_type: grant.scopeType, scope_id: grant.scopeId }, scopeLookupContext)}`)
      }
    }
    return byKey
  }, [accessContext?.effectivePermissions, organizations, organizationUnits, accessibleLocations, accessContext?.tenant, accessContext?.tenantId])
  const selectedUserGrantByKey = useMemo(() => {
    const byKey = new Map<string, string>()
    for (const grant of selectedUserPermissionGrants) {
      if (!byKey.has(grant.permissionKey)) byKey.set(grant.permissionKey, `${grant.roleLabel} / ${grant.scopeLabel}`)
    }
    return byKey
  }, [selectedUserPermissionGrants])

  useEffect(() => {
    if (!selectedOrgId || !canViewUsers) {
      setRoles([])
      return
    }
    let mounted = true
    setLoading(true)
    setError('')
    Promise.all([
      listOrganizationInvites(selectedOrgId).catch(() => [] as OrganizationInviteSummary[]),
      listOrganizationRoles(selectedOrgId).catch(() => [] as TenantRoleSummary[]),
      listAdminCapabilities().catch(() => [] as AdminCapabilitySummary[]),
    ])
      .then(([inviteRows, roleRows, capabilityRows]) => {
        if (!mounted) return
        setInvites(inviteRows)
        setRoles(roleRows)
        setCapabilities(capabilityRows)
      })
      .catch((err: any) => mounted && setError(err?.response?.data?.message || 'Unable to load organization users.'))
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [canViewUsers, selectedOrgId])

  useEffect(() => { userList.setFilter('status', statusFilter || undefined) }, [statusFilter])
  useEffect(() => {
    setSelectedUserId((current) => (current && users.some((user) => user.id === current) ? current : users[0]?.id || ''))
  }, [users])

  useEffect(() => {
    if (!selectedOrgId || !selectedUserId || !canViewUsers) {
      setSelectedUserAccess([])
      return
    }
    let mounted = true
    listOrganizationUserAccess(selectedOrgId, selectedUserId)
      .then((rows) => mounted && setSelectedUserAccess(rows))
      .catch(() => mounted && setSelectedUserAccess([]))
    return () => { mounted = false }
  }, [canViewUsers, selectedOrgId, selectedUserId])

  const reload = async (refreshContext = true) => {
    if (!selectedOrgId) return
    const roleRows = await listOrganizationRoles(selectedOrgId).catch(() => roles)
    await userList.refresh()
    setInvites(await listOrganizationInvites(selectedOrgId).catch(() => invites))
    setRoles(roleRows)
    setCapabilities(await listAdminCapabilities().catch(() => capabilities))
    if (selectedUserId) setSelectedUserAccess(await listOrganizationUserAccess(selectedOrgId, selectedUserId))
    if (refreshContext) await onRefresh()
  }

  const openUser = (user: OrganizationUserSummary) => {
    setSelectedUserId(user.id)
    setShowUserDrawer(true)
    setMessage('')
    setError('')
    setInviteLink('')
  }

  const openEdit = (user: OrganizationUserSummary) => {
    setSelectedUserId(user.id)
    setEditForm({ firstName: user.first_name || '', lastName: user.last_name || '', phone: user.phone || '' })
    setShowEditModal(true)
    setMessage('')
    setError('')
  }

  const openAssignRole = (user?: OrganizationUserSummary | null) => {
    if (user) setSelectedUserId(user.id)
    setRoleForm(blankRoleForm())
    setShowRoleModal(true)
    setMessage('')
    setError('')
  }

  const openCreateTenantRole = () => {
    setTenantRoleForm(blankTenantRoleForm())
    setShowTenantRoleModal(true)
    setMessage('')
    setError('')
  }

  const openEditTenantRole = (role: TenantRoleSummary) => {
    setTenantRoleForm({
      id: role.id,
      roleKey: role.role_key || '',
      label: role.label || role.role_key || '',
      description: role.description || '',
      permissionKeys: [...(role.permission_keys || [])],
    })
    setShowTenantRoleModal(true)
    setMessage('')
    setError('')
  }

  const toggleTenantRolePermission = (permissionKey: string) => {
    setTenantRoleForm((current) => {
      const selected = new Set(current.permissionKeys)
      if (selected.has(permissionKey)) selected.delete(permissionKey)
      else selected.add(permissionKey)
      return { ...current, permissionKeys: Array.from(selected).sort() }
    })
  }

  const handleSaveTenantRole = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canManageRoles) return
    if (!tenantRoleForm.label.trim()) {
      setError(t('Role name is required.'))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      if (tenantRoleForm.id) {
        await updateTenantRole(tenantRoleForm.id, {
          label: tenantRoleForm.label.trim(),
          description: tenantRoleForm.description.trim() || null,
          permissionKeys: tenantRoleForm.permissionKeys,
        })
        setMessage(t('Role updated.'))
      } else {
        await createTenantRole({
          roleKey: tenantRoleForm.roleKey.trim() || undefined,
          label: tenantRoleForm.label.trim(),
          description: tenantRoleForm.description.trim() || null,
          permissionKeys: tenantRoleForm.permissionKeys,
        })
        setMessage(t('Role created.'))
      }
      setShowTenantRoleModal(false)
      setTenantRoleForm(blankTenantRoleForm())
      await reload()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to save role.'))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteTenantRole = async () => {
    if (!confirmDeleteRole || !canManageRoles) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await deleteTenantRole(confirmDeleteRole.id)
      setConfirmDeleteRole(null)
      await reload()
      setMessage(t('Role deleted.'))
    } catch (err: any) {
      const dependencies = err?.response?.data?.dependencies
      const suffix = Array.isArray(dependencies) && dependencies.length
        ? ' ' + t('Dependencies: {dependencies}.', { dependencies: dependencies.map((item: any) => `${item.type}: ${item.count}`).join(', ') })
        : ''
      setError((err?.response?.data?.message || t('Unable to delete role.')) + suffix)
    } finally {
      setSaving(false)
    }
  }

  const handleInvite = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedOrgId || !canInviteUsers) return
    const scopeType = inviteForm.scopeType || 'organization'
    if (inviteForm.roleId && scopeType !== 'organization' && !inviteForm.scopeId) {
      setError(t('Choose a {scope} scope before sending the invitation.', { scope: t(scopeTypeLabel(scopeType)) }))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const createdInvite = await inviteOrganizationUser(selectedOrgId, {
        ...inviteForm,
        email: inviteForm.email.trim(),
        firstName: inviteForm.firstName?.trim() || undefined,
        lastName: inviteForm.lastName?.trim() || undefined,
        phone: inviteForm.phone?.trim() || undefined,
        roleId: inviteForm.roleId || undefined,
        scopeType,
        scopeId: scopeType === 'organization' ? selectedOrgId : inviteForm.scopeId,
      })
      setShowInvite(false)
      setInviteForm(blankInviteForm())
      await reload()
      const token = (createdInvite as any)?.token
      setInviteLink(token ? `${window.location.origin}/org-invites/${token}` : '')
      setMessage(token ? t('Invitation created. Share the invite link with the member.') : t('Invitation created.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to invite user.'))
    } finally {
      setSaving(false)
    }
  }

  const handleCreateUser = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedOrgId || !canManageUsers) return
    const scopeType = inviteForm.scopeType || 'organization'
    if (!inviteForm.roleId) {
      setError(t('Choose an initial role before adding an active user.'))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await createOrganizationUser(selectedOrgId, {
        ...inviteForm,
        email: inviteForm.email.trim(),
        firstName: inviteForm.firstName?.trim() || undefined,
        lastName: inviteForm.lastName?.trim() || undefined,
        phone: inviteForm.phone?.trim() || undefined,
        roleId: inviteForm.roleId || undefined,
        scopeType,
        scopeId: scopeType === 'organization' ? selectedOrgId : inviteForm.scopeId,
      })
      setShowCreateUser(false)
      setInviteForm(blankInviteForm())
      await reload()
      setMessage(t('User added.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to add user.'))
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedOrgId || !selectedUser || !canManageUsers || !canEditSelectedGlobalProfile) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await updateOrganizationUser(selectedOrgId, selectedUser.id, {
        firstName: editForm.firstName.trim() || null,
        lastName: editForm.lastName.trim() || null,
        phone: editForm.phone.trim() || null,
      })
      setShowEditModal(false)
      await reload()
      setMessage(t('User updated.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to update user.'))
    } finally {
      setSaving(false)
    }
  }

  const handleAssignRole = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedOrgId || !selectedUser || !canManageRoles) return
    if (!roleForm.roleId.trim()) {
      setError(t('Choose a role before assigning access.'))
      return
    }
    if (!canUseProtectedPlatformRoles && isProtectedRole(roles.find((role) => role.id === roleForm.roleId))) {
      setError(t('Only system administrators can assign platform roles.'))
      return
    }
    const scopeType = roleForm.scopeType || 'organization'
    if (!['organization','tenant'].includes(scopeType) && !roleForm.scopeId) {
      setError(t('Choose a {scope} scope before assigning access.', { scope: t(scopeTypeLabel(scopeType)) }))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      if (scopeType === 'tenant') {
        await createScopedRoleAssignment({
          userId: selectedUser.id,
          roleId: roleForm.roleId,
          scopeType: 'tenant',
          scopeId: null,
          inheritsDown: true,
          startsAt: roleForm.startsAt,
          endsAt: roleForm.endsAt,
        })
      } else {
        await assignOrganizationUserRole(selectedOrgId, selectedUser.id, {
          ...roleForm,
          scopeType,
          scopeId: scopeType === 'organization' ? selectedOrgId : roleForm.scopeId,
        })
      }
      setShowRoleModal(false)
      setRoleForm(blankRoleForm())
      await reload()
      setMessage(t('Scoped role assigned.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to assign role.'))
    } finally {
      setSaving(false)
    }
  }

  const handleStatus = async () => {
    if (!selectedOrgId || !confirmStatus || !canManageUsers) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await updateOrganizationUser(selectedOrgId, confirmStatus.user.id, { status: confirmStatus.status, active: confirmStatus.status === 'active' })
      setConfirmStatus(null)
      await reload()
      setMessage(confirmStatus.status === 'active' ? t('User reactivated.') : t('User suspended.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to update user status.'))
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveRole = async () => {
    if (!selectedOrgId || !selectedUser || !confirmRevoke || !canManageRoles) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await revokeOrganizationUserRole(selectedOrgId, selectedUser.id, confirmRevoke.id)
      setConfirmRevoke(null)
      await reload()
      setMessage(t('Scoped role revoked.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to revoke role.'))
    } finally {
      setSaving(false)
    }
  }

  const handleResetPassword = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedOrgId || !resetPasswordFor || !canManageUsers || !canSetGlobalPassword) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await resetOrganizationUserPassword(selectedOrgId, resetPasswordFor.id, passwordForm.password)
      setResetPasswordFor(null)
      setPasswordForm({ password: '' })
      if (resetPasswordFor.id === accessContext?.currentUser?.id) {
        await logout()
        window.location.assign('/login')
        return
      }
      await reload(false)
      setMessage(t('Password reset.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to reset password.'))
    } finally {
      setSaving(false)
    }
  }

  const handleRevokeInvite = async () => {
    if (!confirmInviteRevoke) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await revokeOrganizationInvite(confirmInviteRevoke.id)
      setConfirmInviteRevoke(null)
      await reload()
      setMessage(t('Invite revoked.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to revoke invite.'))
    } finally {
      setSaving(false)
    }
  }

  if (!canViewUsers) {
    return <p className="admin-disabled-callout">{t('Users & Access requires user or role view permission.')}</p>
  }

  return (
    <div className="stack">
      <div className="admin-panel-toolbar">
        <div className="admin-select-row">
          <label htmlFor="users-org-select" className="helper">{t('Organization')}</label>
          <select id="users-org-select" className="input" value={selectedOrgId} onChange={(event) => onSelectOrg(event.target.value)}>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>{org.name} - {t(formatOrgType(org.org_type))}</option>
            ))}
          </select>
        </div>
        <div className="inline-group">
          <button type="button" className="btn btn-secondary" disabled={!canManageUsers || !selectedOrgId} title={!selectedOrgId ? t('Select an organization before adding a user.') : !canManageUsers ? t('Adding users requires user management permission.') : undefined} onClick={() => { setInviteForm(blankInviteForm()); setShowCreateUser(true) }}>
            {t('Add user')}
          </button>
          <button type="button" className="btn btn-primary" disabled={!canInviteUsers || !selectedOrgId} title={!selectedOrgId ? t('Select an organization before inviting a user.') : !canInviteUsers ? t('Inviting users requires invite or user management permission.') : undefined} onClick={() => { setInviteForm(blankInviteForm()); setShowInvite(true) }}>
            {t('Invite user')}
          </button>
        </div>
      </div>
      {!selectedOrgId ? <p className="admin-disabled-callout">{t('Select an organization before changing users or scoped access.')}</p> : null}
      {selectedOrg ? <p className="helper">{t('Operating in {orgName}. Role choices below are limited to this tenant and organization context; backend permission checks still enforce every mutation.', { orgName: selectedOrg.name })}</p> : null}

      <div className="admin-tabs" role="tablist" aria-label={t('Users and access sections')}>
        {tabs.map((tab) => (
          <button key={tab.id} type="button" className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
            {t(tab.label)}
          </button>
        ))}
      </div>

      {activeTab === 'users' ? (
        <div className="inline-group" role="tablist" aria-label={t('User account segments')}>
          {(['admins', 'workers', 'inactive'] as UserSegmentId[]).map((segment) => (
            <button key={segment} type="button" className={`admin-filter ${activeSegment === segment ? 'active' : ''}`} onClick={() => setActiveSegment(segment)}>
              {t(userSegmentLabel(segment))} <span className="helper">({segmentedUsers[segment].length})</span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="admin-filter-row">
        <input className="input" placeholder={t('Search name or email')} value={userList.search} onChange={(event) => userList.setSearch(event.target.value)} />
        <select className="input admin-inline-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="">{t('All statuses')}</option>
          <option value="invited">{t('Invited')}</option>
          <option value="active">{t('Active')}</option>
          <option value="disabled">{t('Disabled')}</option>
        </select>
      </div>

      {message ? <p className="success-text">{message}</p> : null}
      {inviteLink ? <p className="helper">{t('Invite link:')} <code>{inviteLink}</code></p> : null}
      <ErrorBanner message={error} />
      {loading ? <LoadingState label={t('Loading users...')} /> : null}

      {activeTab === 'users' || activeTab === 'invitations' ? (
        <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
          {activeTab === 'invitations' ? (
            <table className="workspace-table">
              <thead><tr><th>{t('Email')}</th><th>{t('Role')}</th><th>{t('Scope')}</th><th>{t('Status')}</th><th>{t('Expires')}</th><th>{t('Created')}</th><th>{t('Actions')}</th></tr></thead>
              <tbody>
                {invites.map((invite) => (
                  <tr key={invite.id}>
                    <td>{invite.email}</td>
                    <td>{invite.role_label || invite.role_key || t('No role yet')}</td>
                    <td>{formatScopeLabel({ scope_type: invite.scope_type, scope_id: invite.scope_id === selectedOrgId ? selectedOrgId : invite.scope_id }, scopeLookupContext)}</td>
                    <td><span className="status-pill">{t(titleCase(invite.status))}</span></td>
                    <td>{formatDate(invite.expires_at || undefined)}</td>
                    <td>{formatDate(invite.created_at)}</td>
                    <td>
                      <button type="button" className="btn btn-ghost" disabled={!canManageUsers || invite.status !== 'pending'} onClick={() => setConfirmInviteRevoke(invite)}>{t('Revoke')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
          <table className="workspace-table">
            <thead>{activeSegment === 'workers' ? (
              <tr><th><SortableHeader label={t('Worker name')} sortKey="name" activeSortBy={userList.sortBy} sortDir={userList.sortDir} onSort={userList.setSort} /></th><th>{t('Email / phone')}</th><th>{t('Linked Staff profile')}</th><th>{t('Organization relationship')}</th><th>{t('Account status')}</th><th>{t('App access status')}</th><th>{t('Badges')}</th><th>{t('Actions')}</th></tr>
            ) : (
              <tr><th><SortableHeader label={t('Name')} sortKey="name" activeSortBy={userList.sortBy} sortDir={userList.sortDir} onSort={userList.setSort} /></th><th><SortableHeader label={t('Email')} sortKey="email" activeSortBy={userList.sortBy} sortDir={userList.sortDir} onSort={userList.setSort} /></th><th>{t('Phone')}</th><th>{t('Account status')}</th><th>{t('Access role summary')}</th><th>{t('Scope summary')}</th><th><SortableHeader label={t('Last sign-in')} sortKey="created" activeSortBy={userList.sortBy} sortDir={userList.sortDir} onSort={userList.setSort} /></th><th>{t('Badges')}</th><th>{t('Actions')}</th></tr>
            )}</thead>
            <tbody>
              {displayedUsers.map((user) => (
                <tr key={user.id} className={selectedUserId === user.id ? 'is-selected' : ''}>
                  {activeSegment === 'workers' ? (
                    <>
                      <td>{workerDisplayNameForUser(user)}</td>
                      <td>{user.email || user.phone || '-'}</td>
                      <td>{workerProfileIdForUser(user) ? <span className="status-pill">{t('Linked')}</span> : <span className="status-pill">{t('No Staff profile')}</span>}</td>
                      <td>{[workerOrganizationNameForUser(user) || selectedOrg?.name, workerRelationshipTypeForUser(user) ? t(titleCase(workerRelationshipTypeForUser(user))) : null].filter(Boolean).join(' / ') || '-'}</td>
                      <td><span className="status-pill">{t(titleCase(user.membership_status || (user.active ? 'active' : 'disabled')))}</span></td>
                      <td>{user.active === false || user.membership_status === 'disabled' ? t('Account disabled') : user.membership_status === 'invited' ? t('Invite pending') : workerProfileIdForUser(user) ? t('App access active') : t('No login account')}</td>
                      <td>{userAccessBadges(user, t).map((badge) => <span key={badge} className="admin-chip">{badge}</span>)}</td>
                    </>
                  ) : (
                    <>
                      <td>{userName(user)}</td>
                      <td>{user.email || '-'}</td>
                      <td>{user.phone || '-'}</td>
                      <td><span className="status-pill">{t(titleCase(user.membership_status || (user.active ? 'active' : 'disabled')))}</span></td>
                      <td>{roleSummary(user, t)}</td>
                      <td>{scopeSummary(user, t)}</td>
                      <td>{formatDate(user.updated_at || user.created_at)}</td>
                      <td>{userAccessBadges(user, t).map((badge) => <span key={badge} className="admin-chip">{badge}</span>)}</td>
                    </>
                  )}
                  <td>
                    <EntityActionMenu>
                      <button type="button" className="btn btn-ghost" onClick={() => openUser(user)}>{t('View details')}</button>
                      {workerProfileIdForUser(user) ? <a className="btn btn-ghost" href={`/admin?workspace=workers&workerProfileId=${encodeURIComponent(workerProfileIdForUser(user) || '')}`}>{t('View Staff profile')}</a> : null}
                      <button type="button" className="btn btn-ghost" disabled={!canManageUsers || (!canSetGlobalPassword && user.id !== accessContext?.currentUser?.id)} title={t('Global profiles belong to their account owners.')} onClick={() => openEdit(user)}>{t('Edit user')}</button>
                      {activeSegment !== 'workers' ? <button type="button" className="btn btn-ghost" disabled={!canManageRoles} onClick={() => openAssignRole(user)}>{t('Assign role')}</button> : null}
                      <button type="button" className="btn btn-ghost" disabled={!canManageUsers || !canSetGlobalPassword} title={t('Password changes require account-owner recovery or platform security administration.')} onClick={() => { setResetPasswordFor(user); setPasswordForm({ password: '' }) }}>{t('Reset password')}</button>
                      {accessContext?.isSystemSuperAdmin && <button type="button" className="btn btn-ghost" onClick={() => setGlobalPermissionsFor(user)}>{t('Global platform permissions')}</button>}
                      {user.membership_status === 'disabled' ? (
                        <button type="button" className="btn btn-ghost" disabled={!canManageUsers || saving} onClick={() => setConfirmStatus({ user, status: 'active' })}>{t('Reactivate')}</button>
                      ) : (
                        <button type="button" className="btn btn-ghost" disabled={!canManageUsers || saving} onClick={() => setConfirmStatus({ user, status: 'disabled' })}>{t('Suspend')}</button>
                      )}
                    </EntityActionMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
          {activeTab !== 'invitations' ? <PaginationControls pagination={userList.pagination} onPageChange={userList.setPage} onPageSizeChange={userList.setPageSize} /> : null}
          {activeTab === 'invitations' && invites.length === 0 ? <EmptyState title={t('No pending invites')} description={t('Generated invite links for this organization will appear here.')} /> : null}
          {activeTab !== 'invitations' && displayedUsers.length === 0 ? <EmptyState title={t('No users found')} description={t('No users match the current segment, organization, status, and search filters.')} /> : null}
        </div>
      ) : null}

      {activeTab === 'roles' ? (
        <div className="mapper-grid">
          <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
            <div className="admin-panel-toolbar">
              <h3 style={{ margin: 0 }}>{t('Roles')}</h3>
              <button type="button" className="btn btn-primary" disabled={!canManageRoles} onClick={openCreateTenantRole}>{t('Create role')}</button>
            </div>
            {roles.map((role) => (
              <div key={role.id} className="admin-list-row">
                <strong>{role.label || role.role_key}</strong>
                <span className="text-muted">{role.description || role.role_key}</span>
                <span className="helper">{t('{count} permissions', { count: role.permission_count || 0 })}</span>
                <div className="inline-group">
                  <button type="button" className="btn btn-ghost" disabled={!canManageRoles} onClick={() => openEditTenantRole(role)}>{t('Edit')}</button>
                  <button type="button" className="btn btn-ghost" disabled={!canManageRoles || saving} onClick={() => setConfirmDeleteRole(role)}>{t('Delete if safe')}</button>
                </div>
              </div>
            ))}
            {roles.length === 0 ? <EmptyState title={t('No roles found')} description={t('Create a tenant role and assign feature permissions.')} /> : null}
          </article>
          <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
            <h3 style={{ margin: 0 }}>{t('Feature permission catalog')}</h3>
            {capabilitiesByGroup.map(([group, groupCapabilities]) => (
              <div key={group}>
                <h4>{t(group)}</h4>
                {groupCapabilities.map((capability) => (
                  <div key={capability.capability} className="admin-list-row">
                    <strong>{capability.label}</strong>
                    <span className="helper">{capability.permissionKeys.join(', ')}</span>
                  </div>
                ))}
              </div>
            ))}
          </article>
        </div>
      ) : null}

      {activeTab === 'scope' ? (
        <div className="mapper-grid">
          <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
            <div className="admin-panel-toolbar">
              <h3 style={{ margin: 0 }}>{selectedUser ? userName(selectedUser) : t('Select a user')}</h3>
              <button type="button" className="btn btn-primary" disabled={!selectedUser || !canManageRoles} onClick={() => openAssignRole(selectedUser)}>{t('Assign role')}</button>
            </div>
            {selectedUserAccess.map((assignment) => (
              <div key={assignment.id} className="admin-list-row">
                <strong>{assignment.role_label || assignment.role_key || assignment.role_id}</strong>
                <span className="text-muted">{t(scopeGroupLabel(assignment.scope_type))}: {scopeLabelForAssignment(assignment)}</span>
                <span className="helper">{t(scopeImpactLabel(assignment.scope_type, assignment.inherits_down))}</span>
                <button type="button" className="btn btn-ghost" disabled={!canManageRoles || saving} onClick={() => setConfirmRevoke(assignment)}>{t('Remove role')}</button>
              </div>
            ))}
            {selectedUserAccess.length === 0 ? <EmptyState title={t('No visible role assignments')} description={t('This user has no scoped role assignments visible in the selected organization.')} /> : null}
          </article>
          <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
            <h3 style={{ margin: 0 }}>{t('What can this user see?')}</h3>
            <p className="helper">{t('Worker availability, reliability, and attendance events are visible to anyone who can read the worker profile through {workersPermission} or {assignmentsPermission}. Access can be direct, inherited from tenant, organization, parent OU, or location, and denials are missing worker visibility or outside scope.', { workersPermission: 'tenant.workers.view', assignmentsPermission: 'tenant.job_assignments.view' })}</p>
            <table className="workspace-table">
              <thead><tr><th>{t('Feature')}</th><th>{t('Selected user')}</th><th>{t('Required permission key')}</th><th>{t('Your current access')}</th><th>{t('Source role / scope')}</th></tr></thead>
              <tbody>
                {featurePermissionGroups.map((group) => {
                  const selectedKey = group.keys.find((key) => selectedUserPermissionKeys.has(key))
                  const currentKey = group.keys.find((key) => currentAdminPermissionKeys.has(key))
                  const displayKey = selectedKey || currentKey || group.keys[0]
                  return (
                    <tr key={group.feature}>
                      <td><strong>{t(group.feature)}</strong><div className="helper">{t(group.source)}</div></td>
                      <td><span className={`status-pill status-pill--${selectedKey ? 'approved' : 'warning'}`}>{selectedKey ? t('Visible') : t('Hidden')}</span></td>
                      <td><code>{displayKey}</code>{group.keys.length > 1 ? <div className="helper">{t('Also accepted: {keys}', { keys: group.keys.filter((key) => key !== displayKey).join(', ') })}</div> : null}</td>
                      <td><span className={`status-pill status-pill--${currentKey ? 'approved' : 'warning'}`}>{currentKey ? 'You have it' : 'You do not have it'}</span></td>
                      <td>{selectedKey ? selectedUserGrantByKey.get(selectedKey) : currentKey ? currentAdminGrantByKey.get(currentKey) : t('No visible grant in this context')}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </article>
        </div>
      ) : null}

      {activeTab === 'audit' ? (
        <article className="panel workspace-panel admin-empty-state stack">
          <h3 style={{ margin: 0 }}>{t('Audit / Recent Changes')}</h3>
          <p className="text-muted">{t('Dedicated audit events are not part of the current PostgreSQL schema. The backend still enforces every user and role mutation by tenant and organization scope.')}</p>
        </article>
      ) : null}

      <DetailDrawer
        title={selectedUser ? userName(selectedUser) : t('User details')}
        subtitle={selectedOrg ? `${selectedOrg.name} - ${t(formatOrgType(selectedOrg.org_type))}` : undefined}
        isOpen={showUserDrawer && Boolean(selectedUser)}
        onClose={() => setShowUserDrawer(false)}
        actions={(
          <>
            <button type="button" className="btn btn-primary" disabled={!selectedUser || !canManageUsers} onClick={() => selectedUser && openEdit(selectedUser)}>{t('Edit')}</button>
            <button type="button" className="btn btn-secondary" disabled={!selectedUser || !canManageRoles} onClick={() => openAssignRole(selectedUser)}>{t('Assign role')}</button>
          </>
        )}
      >
        {selectedUser ? (
          <div className="stack">
            <div className="admin-detail-grid">
              <span>{t('Email')}</span><strong>{selectedUser.email || '-'}</strong>
              <span>{t('Phone')}</span><strong>{selectedUser.phone || '-'}</strong>
              <span>{t('Status')}</span><strong>{t(titleCase(selectedUser.membership_status || (selectedUser.active ? 'active' : 'disabled')))}</strong>
              <span>{t('Membership')}</span><strong>{selectedUser.membership_type ? t(titleCase(selectedUser.membership_type)) : '-'}</strong>
              <span>{t('Joined')}</span><strong>{formatDate(selectedUser.joined_at || selectedUser.created_at)}</strong>
              <span>{t('Updated')}</span><strong>{formatDate(selectedUser.updated_at)}</strong>
            </div>
            <div>
              <h4>{t('Scoped permissions')}</h4>
              {selectedUserAccess.map((assignment) => (
                <div key={assignment.id} className="admin-list-row">
                  <strong>{assignment.role_label || assignment.role_key || assignment.role_id}</strong>
                  <span>{t(scopeGroupLabel(assignment.scope_type))}: {scopeLabelForAssignment(assignment)}</span>
                  <span className="helper">{t(scopeImpactLabel(assignment.scope_type, assignment.inherits_down))}</span>
                </div>
              ))}
              {selectedUserAccess.length === 0 ? <p className="helper">{t('No scoped role assignments visible for this user.')}</p> : null}
            </div>
            <div>
              <h4>{t('Permission visibility hints')}</h4>
              <p className="helper">{t('Worker availability, reliability, and attendance events follow worker profile visibility through {workersPermission} or {assignmentsPermission} plus backend inherited worker scope. The backend reports direct, inherited from tenant, organization, parent OU, location, missing worker visibility, or outside scope.', { workersPermission: 'tenant.workers.view', assignmentsPermission: 'tenant.job_assignments.view' })}</p>
            </div>
          </div>
        ) : null}
      </DetailDrawer>

      <FormModal
        title={t('Invite user')}
        description={selectedOrg ? t('Create access inside {orgName}.', { orgName: selectedOrg.name }) : undefined}
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
        size="lg"
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setShowInvite(false)} disabled={saving}>{t('Cancel')}</button>
            <button type="submit" form="users-invite-form" className="btn btn-primary" disabled={saving || !selectedOrg || !canInviteUsers}>{saving ? t('Inviting...') : t('Send invitation')}</button>
          </>
        )}
      >
        <form id="users-invite-form" className="stack" onSubmit={handleInvite}>
          <div className="admin-form-grid">
            <div className="form-row"><label>{t('Email')}</label><input className="input" value={inviteForm.email} onChange={(event) => setInviteForm({ ...inviteForm, email: event.target.value })} /></div>
            <div className="form-row"><label>{t('Phone')}</label><input className="input" value={inviteForm.phone || ''} onChange={(event) => setInviteForm({ ...inviteForm, phone: event.target.value })} /></div>
            <div className="form-row"><label>{t('First name')}</label><input className="input" value={inviteForm.firstName || ''} onChange={(event) => setInviteForm({ ...inviteForm, firstName: event.target.value })} /></div>
            <div className="form-row"><label>{t('Last name')}</label><input className="input" value={inviteForm.lastName || ''} onChange={(event) => setInviteForm({ ...inviteForm, lastName: event.target.value })} /></div>
            <div className="form-row">
              <label>{t('Role')}</label>
              <select className="input" value={inviteForm.roleId || ''} onChange={(event) => setInviteForm({ ...inviteForm, roleId: event.target.value || undefined })}>
                <option value="">{t('No role yet')}</option>
                {organizationRoleOptions.map((role) => <option key={role.id} value={role.id}>{role.label}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label>{t('Scope type')}</label>
              <select className="input" value={inviteForm.scopeType || 'organization'} onChange={(event) => setInviteForm({ ...inviteForm, scopeType: event.target.value as OrganizationUserInvitePayload['scopeType'], scopeId: null })} disabled={!inviteForm.roleId}>
                <option value="organization">{t('Organization')}</option>
                <option value="organization_unit">{t('Organization unit')}</option>
                <option value="location">{t('Location')}</option>
              </select>
            </div>
            <div className="form-row">
              <label>{t('Scope')}</label>
              <select className="input" value={inviteForm.scopeId || ''} onChange={(event) => setInviteForm({ ...inviteForm, scopeId: event.target.value || null })} disabled={!inviteForm.roleId || inviteForm.scopeType === 'organization'}>
                <option value="">{inviteForm.scopeType === 'organization' ? selectedOrg?.name || t('Organization') : t('Select scope')}</option>
                <optgroup label={t(scopeGroupLabel(inviteForm.scopeType))}>
                  {inviteScopeOptions.map((scope) => <option key={scope.id} value={scope.id} disabled={scope.disabled}>{scope.label}</option>)}
                </optgroup>
              </select>
              {inviteForm.roleId ? <p className="helper">{t(scopeImpactLabel(inviteForm.scopeType, inviteForm.inheritsDown))}</p> : null}
              {inviteForm.roleId && inviteForm.scopeType !== 'organization' && inviteScopeOptions.length === 0 ? <p className="admin-disabled-callout">{t('No valid {scope} scopes are visible for this organization.', { scope: t(scopeTypeLabel(inviteForm.scopeType)) })}</p> : null}
            </div>
            <label className="inline-group" style={{ alignItems: 'center' }}><input type="checkbox" checked={inviteForm.inheritsDown !== false} onChange={(event) => setInviteForm({ ...inviteForm, inheritsDown: event.target.checked })} /> <span>{t('Inherits down')}</span></label>
          </div>
        </form>
      </FormModal>

      <FormModal
        title={t('Add user')}
        description={selectedOrg ? t('Create active access inside {orgName}.', { orgName: selectedOrg.name }) : undefined}
        isOpen={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        size="lg"
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setShowCreateUser(false)} disabled={saving}>{t('Cancel')}</button>
            <button type="submit" form="users-create-form" className="btn btn-primary" disabled={saving || !selectedOrg || !inviteForm.roleId}>{saving ? t('Adding...') : t('Add user')}</button>
          </>
        )}
      >
        <form id="users-create-form" className="stack" onSubmit={handleCreateUser}>
          <div className="admin-form-grid">
            <div className="form-row"><label>{t('Email')}</label><input className="input" value={inviteForm.email} onChange={(event) => setInviteForm({ ...inviteForm, email: event.target.value })} /></div>
            <div className="form-row"><label>{t('Phone')}</label><input className="input" value={inviteForm.phone || ''} onChange={(event) => setInviteForm({ ...inviteForm, phone: event.target.value })} /></div>
            <div className="form-row"><label>{t('First name')}</label><input className="input" value={inviteForm.firstName || ''} onChange={(event) => setInviteForm({ ...inviteForm, firstName: event.target.value })} /></div>
            <div className="form-row"><label>{t('Last name')}</label><input className="input" value={inviteForm.lastName || ''} onChange={(event) => setInviteForm({ ...inviteForm, lastName: event.target.value })} /></div>
            <div className="form-row">
              <label>{t('Role')}</label>
              <select className="input" value={inviteForm.roleId || ''} onChange={(event) => setInviteForm({ ...inviteForm, roleId: event.target.value || undefined })}>
                <option value="">{t('Select role')}</option>
                {organizationRoleOptions.map((role) => <option key={role.id} value={role.id}>{role.label}</option>)}
              </select>
            </div>
          </div>
        </form>
      </FormModal>

      <FormModal
        title={t('Edit user')}
        isOpen={showEditModal && Boolean(selectedUser)}
        onClose={() => setShowEditModal(false)}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setShowEditModal(false)} disabled={saving}>{t('Cancel')}</button>
            <button type="submit" form="users-edit-form" className="btn btn-primary" disabled={saving || !canManageUsers || !canEditSelectedGlobalProfile}>{saving ? t('Saving...') : t('Save user')}</button>
          </>
        )}
      >
        <form id="users-edit-form" className="admin-form-grid" onSubmit={handleEdit}>
          <div className="form-row"><label>{t('First name')}</label><input className="input" value={editForm.firstName} disabled={!canEditSelectedGlobalProfile} onChange={(event) => setEditForm({ ...editForm, firstName: event.target.value })} /></div>
          <div className="form-row"><label>{t('Last name')}</label><input className="input" value={editForm.lastName} disabled={!canEditSelectedGlobalProfile} onChange={(event) => setEditForm({ ...editForm, lastName: event.target.value })} /></div>
          <div className="form-row"><label>{t('Phone')}</label><input className="input" value={editForm.phone} disabled={!canEditSelectedGlobalProfile} onChange={(event) => setEditForm({ ...editForm, phone: event.target.value })} /></div>
        </form>
      </FormModal>

      <FormModal
        title={t('Assign scoped role')}
        description={selectedUser ? t('Access for {userName} is limited to the selected tenant organization context.', { userName: userName(selectedUser) }) : undefined}
        isOpen={showRoleModal && Boolean(selectedUser)}
        onClose={() => setShowRoleModal(false)}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setShowRoleModal(false)} disabled={saving}>{t('Cancel')}</button>
            <button type="submit" form="users-role-form" className="btn btn-primary" disabled={saving || !selectedUser || !canManageRoles}>{saving ? t('Saving...') : t('Assign role')}</button>
          </>
        )}
      >
        <form id="users-role-form" className="stack" onSubmit={handleAssignRole}>
          <div className="admin-form-grid">
            <div className="form-row">
              <label>{t('Role')}</label>
              <select className="input" value={roleForm.roleId} onChange={(event) => setRoleForm({ ...roleForm, roleId: event.target.value })}>
                <option value="">{t('Select role')}</option>
                {roleOptions.map((role) => <option key={role.id} value={role.id}>{role.label}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label>{t('Scope type')}</label>
              <select className="input" value={roleForm.scopeType || 'organization'} onChange={(event) => setRoleForm({ ...roleForm, scopeType: event.target.value as OrganizationUserRolePayload['scopeType'], scopeId: null })}>
                {canAssignTenantScope ? <option value="tenant">{t('Tenant')}</option> : null}
                <option value="organization">{t('Organization')}</option>
                <option value="organization_unit">{t('Organization unit')}</option>
                <option value="location">{t('Location')}</option>
              </select>
              {roleForm.scopeType === 'organization' ? <p className="admin-disabled-callout">{t('Broad assignment: this can inherit through the selected organization, OUs, and linked locations.')}</p> : null}
            </div>
            <div className="form-row">
              <label>{t('Scope')}</label>
              <select className="input" value={roleForm.scopeId || ''} onChange={(event) => setRoleForm({ ...roleForm, scopeId: event.target.value || null })} disabled={roleForm.scopeType === 'organization' || roleForm.scopeType === 'tenant'}>
                <option value="">{roleForm.scopeType === 'tenant' ? accessContext?.tenant?.name || t('Tenant') : roleForm.scopeType === 'organization' ? selectedOrg?.name || t('Organization') : t('Select scope')}</option>
                <optgroup label={t(scopeGroupLabel(roleForm.scopeType))}>
                  {roleScopeOptions.map((scope) => <option key={scope.id} value={scope.id} disabled={scope.disabled}>{scope.label}</option>)}
                </optgroup>
              </select>
              <p className="helper">{t(scopeImpactLabel(roleForm.scopeType, roleForm.inheritsDown))}</p>
              {roleForm.scopeType !== 'organization' && roleScopeOptions.length === 0 ? <p className="admin-disabled-callout">{t('No valid {scope} scopes are visible for this organization.', { scope: t(scopeTypeLabel(roleForm.scopeType)) })}</p> : null}
            </div>
            <label className="inline-group" style={{ alignItems: 'center' }}><input type="checkbox" checked={roleForm.inheritsDown !== false} onChange={(event) => setRoleForm({ ...roleForm, inheritsDown: event.target.checked })} /> <span>{t('Inherits down')}</span></label>
          </div>
        </form>
      </FormModal>

      <FormModal
        title={tenantRoleForm.id ? t('Edit role') : t('Create role')}
        description={t('Select feature permissions for this tenant role.')}
        isOpen={showTenantRoleModal}
        onClose={() => setShowTenantRoleModal(false)}
        size="lg"
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setShowTenantRoleModal(false)} disabled={saving}>{t('Cancel')}</button>
            <button type="submit" form="tenant-role-form" className="btn btn-primary" disabled={saving || !canManageRoles}>{saving ? t('Saving...') : t('Save role')}</button>
          </>
        )}
      >
        <form id="tenant-role-form" className="stack" onSubmit={handleSaveTenantRole}>
          <div className="admin-form-grid">
            {!tenantRoleForm.id ? (
              <div className="form-row">
                <label>{t('Role key')}</label>
                <input className="input" value={tenantRoleForm.roleKey} onChange={(event) => setTenantRoleForm({ ...tenantRoleForm, roleKey: event.target.value })} placeholder="custom_role_key" />
              </div>
            ) : null}
            <div className="form-row">
              <label>{t('Name')}</label>
              <input className="input" value={tenantRoleForm.label} onChange={(event) => setTenantRoleForm({ ...tenantRoleForm, label: event.target.value })} />
            </div>
            <div className="form-row">
              <label>{t('Description')}</label>
              <input className="input" value={tenantRoleForm.description} onChange={(event) => setTenantRoleForm({ ...tenantRoleForm, description: event.target.value })} />
            </div>
          </div>
          <div className="stack">
            {capabilitiesByGroup.map(([group, groupCapabilities]) => (
              <fieldset key={group} className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                <legend>{t(group)}</legend>
                <div className="admin-form-grid">
                  {groupCapabilities.flatMap((capability) => capability.permissionKeys.map((permissionKey) => (
                    <label key={`${capability.capability}-${permissionKey}`} className="inline-group" style={{ alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={tenantRoleForm.permissionKeys.includes(permissionKey)}
                        onChange={() => toggleTenantRolePermission(permissionKey)}
                      />
                      <span>{capability.label} <code>{permissionKey}</code></span>
                    </label>
                  )))}
                </div>
              </fieldset>
            ))}
          </div>
        </form>
      </FormModal>

      <FormModal title={t('Global platform permissions')} isOpen={Boolean(globalPermissionsFor) && Boolean(accessContext?.isSystemSuperAdmin)} onClose={() => setGlobalPermissionsFor(null)}>
        {globalPermissionsFor && <GlobalPlatformPermissionsPanel userId={globalPermissionsFor.id} />}
      </FormModal>

      <FormModal
        title={t('Reset password')}
        description={resetPasswordFor ? t('Set a new password for {userName}.', { userName: userName(resetPasswordFor) }) : undefined}
        isOpen={Boolean(resetPasswordFor) && canSetGlobalPassword}
        onClose={() => setResetPasswordFor(null)}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setResetPasswordFor(null)} disabled={saving}>{t('Cancel')}</button>
            <button type="submit" form="users-reset-password-form" className="btn btn-primary" disabled={saving || passwordForm.password.length < 8}>{saving ? t('Resetting...') : t('Reset password')}</button>
          </>
        )}
      >
        <form id="users-reset-password-form" className="stack" onSubmit={handleResetPassword}>
          <div className="form-row">
            <label>{t('New password')}</label>
            <input className="input" type="password" value={passwordForm.password} onChange={(event) => setPasswordForm({ password: event.target.value })} />
          </div>
        </form>
      </FormModal>

      <ConfirmDialog
        title={confirmStatus?.status === 'active' ? t('Reactivate user?') : t('Suspend user?')}
        message={confirmStatus ? (confirmStatus.status === 'active' ? t('{userName} will be restored to active access.', { userName: userName(confirmStatus.user) }) : t('{userName} will be suspended in this organization scope.', { userName: userName(confirmStatus.user) })) : ''}
        isOpen={Boolean(confirmStatus)}
        confirmLabel={confirmStatus?.status === 'active' ? t('Reactivate') : t('Suspend')}
        destructive={confirmStatus?.status === 'disabled'}
        disabled={saving}
        onCancel={() => setConfirmStatus(null)}
        onConfirm={handleStatus}
      />

      <ConfirmDialog
        title={t('Revoke scoped role?')}
        message={confirmRevoke ? t('{role} will be removed from {userName} for {scope}.', { role: confirmRevoke.role_label || confirmRevoke.role_key || t('This role'), userName: selectedUser ? userName(selectedUser) : t('the selected user'), scope: scopeLabelForAssignment(confirmRevoke) }) : ''}
        isOpen={Boolean(confirmRevoke)}
        confirmLabel={t('Revoke role')}
        destructive
        disabled={saving}
        onCancel={() => setConfirmRevoke(null)}
        onConfirm={handleRemoveRole}
      />
      <ConfirmDialog
        title={t('Delete role if safe?')}
        message={confirmDeleteRole ? t('{role} will be deleted only if it has no active assignments or pending invites.', { role: confirmDeleteRole.label || confirmDeleteRole.role_key }) : ''}
        isOpen={Boolean(confirmDeleteRole)}
        confirmLabel={t('Delete if safe')}
        destructive
        disabled={saving}
        onCancel={() => setConfirmDeleteRole(null)}
        onConfirm={handleDeleteTenantRole}
      />
      <ConfirmDialog
        title={t('Revoke invite?')}
        message={confirmInviteRevoke ? t('The invite for {email} will no longer be accepted.', { email: confirmInviteRevoke.email }) : ''}
        isOpen={Boolean(confirmInviteRevoke)}
        confirmLabel={t('Revoke invite')}
        destructive
        disabled={saving}
        onCancel={() => setConfirmInviteRevoke(null)}
        onConfirm={handleRevokeInvite}
      />
    </div>
  )
}
